import {delay} from '@yandex-delivery/utils'

import type {Fetch} from '../../fetch'
import type {ServerError} from '../../ServerError'
import type {RequestHeaders} from '../../types'

import {
  CSRF_HEADER_NAME,
  MAX_AGE_SECONDS_DELTA,
  MAX_AGE_SECONDS_MIN,
  MAX_AGE_SECONDS_MULTIPLIER
} from './consts'
import {isCsrfResponse, isInvalidCsrfToken} from './lib'
import type {CreateCsrfMiddlewareParams} from './types'

/**
 * Создает CSRF middleware для автоматического добавления токена к запросам
 * @param {CreateCsrfMiddlewareParams} args - Параметры для настройки middleware
 * @returns {Function} - Функция middleware для добавления в цепочку обработки запросов
 * @throws {Error} - Выбрасывает ошибку, если maxAge установлен в 0
 */
function createCsrfMiddleware(args: CreateCsrfMiddlewareParams) {
  const {url} = args

  let csrfToken: string | null = null
  let csrfTokenExpiryTime = -1

  let tokenPromise: Promise<string> | null = null

  /**
   * Функция для получения CSRF токена с сервера
   * @param fetchFn - fetch функция
   * @param headers - Заголовки для запроса токена
   * @param config - Остальная конфигурация запроса
   * @param isRetrying - Признак того, что промис уже выполняется
   *
   * @returns - Promise с токеном
   */
  function getCsrfToken<TReq = unknown, TRes = unknown, TTransformedBody = unknown>(
    fetchFn: Fetch<TReq, TRes, TTransformedBody>,
    headers: RequestHeaders = {},
    config: object = {},
    isRetrying = false
  ): Promise<string> {
    const now = Date.now()
    const isTokenValid = csrfTokenExpiryTime > now

    if (csrfToken && isTokenValid) {
      return Promise.resolve(csrfToken)
    }

    // Если запрос уже идет, возвращаем его
    if (tokenPromise) {
      return tokenPromise
    }

    // Запускаем новый запрос
    tokenPromise = fetchFn({
      ...config,
      method: 'GET',
      body: undefined,
      url,
      headers,
    })
      .then(response => 'body' in response ? response.body : response)
      .then((response) => {
        if (isCsrfResponse(response)) {
          const maxAgeSeconds = response['max-age-seconds']
          const token = response['sk']

          const maxAgeSecondsWithDelta = (maxAgeSeconds - MAX_AGE_SECONDS_DELTA)
          const finalMaxAgeSeconds = maxAgeSecondsWithDelta > MAX_AGE_SECONDS_MIN ? maxAgeSecondsWithDelta : (maxAgeSecondsWithDelta * MAX_AGE_SECONDS_MULTIPLIER)

          const maxAgeMilliseconds = finalMaxAgeSeconds * 1000

          csrfToken = token
          csrfTokenExpiryTime = maxAgeMilliseconds + Date.now()

          return token
        }

        throw new TypeError('Wrong CSRF Response')
      })
      .catch((error) => {
        if (isRetrying) {
          throw error
        }

        csrfToken = null
        csrfTokenExpiryTime = -1
        tokenPromise = null

        return retryFetchCsrfToken(fetchFn, 0, error, headers, config)
      })
      .finally(() => {
        tokenPromise = null
      })

    return tokenPromise
  }

  /**
   * Функция для повторных попыток получения токена с экспоненциальной задержкой
   * @param fetchFn - fetch функция
   * @param attempt - Номер текущей попытки
   * @param error - Ошибка предыдущей попытки
   * @param headers - заголовки
   * @param config - конфиг
   * @returns - Promise с токеном
   */
  function retryFetchCsrfToken<TReq = unknown, TRes = unknown, TTransformedBody = unknown>(
    fetchFn: Fetch<TReq, TRes, TTransformedBody>,
    attempt: number,
    error: ServerError,
    headers: RequestHeaders = {},
    config: object = {},
  ): Promise<string> {
    const MAX_RETRIES = 3
    const INITIAL_DELAY = 1000

    if (attempt >= MAX_RETRIES) {
      throw error
    }

    const delayTime = INITIAL_DELAY * Math.pow(2, attempt)

    return delay(delayTime)
      .then(() => getCsrfToken(fetchFn, headers, config, true))
      .catch((err) => retryFetchCsrfToken(fetchFn, attempt + 1, err, headers, config))
  }

  /**
   * Middleware для добавления CSRF токена к запросам
   * @param  fetchFn - Функция для выполнения запроса
   * @returns - Обертка над функцией запроса с добавлением CSRF токена
   */
  function withCsrfMiddleware<TReq = unknown, TRes = unknown, TTransformedBody = unknown>(
    fetchFn: Fetch<TReq, TRes, TTransformedBody>
  ): Fetch<TReq, TRes, TTransformedBody> {
    return (config) => {
      const {headers = {}, ...restConfig} = config

      return getCsrfToken(fetchFn, headers, config)
        .then(token => fetchFn({
          ...restConfig,
          headers: {
            ...headers,
            [CSRF_HEADER_NAME]: token
          }
        }))
        .catch(error => {
          if (isInvalidCsrfToken(error)) {
            csrfToken = null
            csrfTokenExpiryTime = -1
            tokenPromise = null

            return getCsrfToken(fetchFn, headers, restConfig)
              .then((token) => {
                return fetchFn({
                  ...restConfig,
                  headers: {
                    ...headers,
                    [CSRF_HEADER_NAME]: token
                  }
                })
              })
          }

          throw error
        })
    }
  }

  return withCsrfMiddleware
}

export {createCsrfMiddleware}
